<template>
  <div>
    <div class="showcase-table">
      <div v-if="cartLoading" class="cartOverlay"></div>
      <CRow>
        <table class="table mx-3">
          <thead>
            <tr>
              <th style="width: 8%" class="d-none d-xl-table-cell"></th>

              <SortableHeader
                :styles="'width: 20%'"
                :param="'product_name'"
                :ordering="ordering"
                @sort="sortBy"
              >
                {{ $t("models.productproducer.name") }}
              </SortableHeader>

              <SortableHeader
                :styles="'width: 12%'"
                :param="'category_name'"
                :ordering="ordering"
                @sort="sortBy"
              >
                {{ $t("models.productproducer.category") }}
              </SortableHeader>

              <SortableHeader
                :styles="'width: 15%'"
                :param="'producer_name'"
                :ordering="ordering"
                @sort="sortBy"
              >
                {{ $t("models.productproducer.producer") }}
              </SortableHeader>

              <th style="width: 8%" class="d-none d-xl-table-cell">
                {{ $t("models.productproducer.certifications") }}
              </th>

              <SortableHeader
                :styles="'width: 7%;'"
                :param="'price'"
                :ordering="ordering"
                @sort="sortBy"
              >
                {{ $t("user.marketCart.price") }}
              </SortableHeader>

              <th style="width: 30%; text-align: right">
                {{ $t("family.market.product.cart.qta") }}
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-if="loading">
              <tr v-for="n in 12" :key="n">
                <td style="width: 8%" class="d-none d-xl-table-cell">
                  <SkeletonCell width="95%" />
                </td>
                <td style="width: 20%"><SkeletonCell width="95%" /></td>
                <td style="width: 12%"><SkeletonCell width="95%" /></td>
                <td style="width: 15%"><SkeletonCell width="95%" /></td>
                <td style="width: 7%"><SkeletonCell width="95%" /></td>
                <td style="width: 8%" class="d-none d-xl-table-cell">
                  <SkeletonCell width="95%" />
                </td>
                <td style="width: 30%"><SkeletonCell width="95%" /></td>
              </tr>
            </template>
            <template v-else>
              <template v-if="products.length > 0">
                <tr v-for="(item, index) in products" :key="index">
                  <td
                    @click="onCardClick(item.ppl.productproducer.id)"
                    class="d-none d-xl-table-cell"
                  >
                    <img
                      :src="getImage(item)"
                      height="75"
                      class="border rounded"
                    />
                  </td>
                  <td
                    class="productTemplateTitle"
                    @click="onCardClick(item.ppl.productproducer.id)"
                  >
                    <strong>{{
                      item.ppl.productproducer.translations[lang].name
                    }}</strong
                    ><br />
                    {{ item.ppl.translations[lang].name }}
                  </td>
                  <td>
                    <div>
                      <div class="ui-type-caption text-gray-600 text-uppercase">
                        {{
                          item.ppl.productproducer.category.parent.translations[
                            lang
                          ].name
                        }}
                      </div>
                      {{
                        item.ppl.productproducer.category.translations[lang]
                          .name
                      }}
                    </div>
                  </td>
                  <td>
                    <div>
                      <div
                        @click="
                          openProducerProfile(
                            item.ppl.productproducer.producer.id
                          )
                        "
                      >
                        <strong>{{
                          item.ppl.productproducer.producer.role.name
                        }}</strong
                        ><font-awesome-icon
                          icon="info-circle"
                          class="text-gray-500 ml-2"
                        />
                      </div>
                    </div>
                  </td>
                  <td class="d-none d-xl-table-cell">
                    <div
                      v-if="certifications(item)"
                      v-html="certifications(item)"
                    ></div>
                  </td>
                  <td class="text-left">
                    <div>
                      <strong>{{ getPrice(item) }}</strong>
                      <small v-if="basePriceUnit(item)"
                        ><br />{{ basePriceUnit(item) }}</small
                      >
                      <span
                        class="ml-2 text-warning"
                        v-if="needPriceCorrection()"
                      >
                        <font-awesome-icon icon="balance-scale" />
                      </span>
                    </div>
                  </td>
                  <td class="text-right">
                    <QuantityInput :data="item" ref="quantityInputs" />
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr>
                  <td colspan="7" class="">
                    {{ $t("role.orders.rounds.ddmp.noResults") }}
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </CRow>
      <CRow>
        <CCol>
          <Pagination
            :data="pagination"
            @paginate="loadProducts"
            :maxVisiblePages="7"
          />
        </CCol>
        <CCol xs="2" sm="2" md="1" lg="1" class="ml-auto">
          <UiSelect
            :value.sync="queryFilters.limit.term"
            :options="[12, 50, 100, 200]"
            custom
          />
        </CCol>
      </CRow>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { mapState, mapGetters } from "vuex";
import { formatPrice } from "../../../helpers/common";
import { getCartItemByPpmId, getOptionLabel } from "../../../helpers/common";
import { getPriceUnitOptions, getPwOptions } from "../../../helpers/options";
import get from "lodash/get";
import UiSelect from "../../../common/form/UiSelect";
import { apiGetRQL } from "../../../helpers/apiHelper";
import Pagination from "../../../common/table/Pagination";
import SortableHeader from "../../../common/table/SortableHeaderElement";
import SkeletonCell from "../../../common/table/Skeleton";
import { scrollToTopOfpage } from "../../../helpers/common";

export default {
  name: "MarketShowcaseTable",

  components: {
    UiSelect,
    SortableHeader,
    Pagination,
    SkeletonCell,
    QuantityInput: Vue.component("QuantityInput", {
      template: `
      <div style="width: 90%; float:right;">
        <form @submit.prevent="onSubmit" class="d-flex flex-row justify-content-end align-items-center">
          <CInput
            class="mb-0 mr-1 w-100 variantCartQuantityInput"
            v-model="quantity"
            :append="suffix()"
            type="number"
            :data-variant-id="variantId()"
            min="0"
            :step="1"
            :max="availability() && ( availability().availability_xd ? availability().availability_start : availability().availability )"
            :disabled="( availability() && !availability().availability_xd ) && availability().availability === 0"
          />
          <CButton color="primary" type="submit">
            <font-awesome-icon icon="shopping-cart" />
          </CButton>
        </form>
        <small class="badge badge-gray" v-if="availability() && availability().availability_xd">
          Max {{ availability().availability_start }} {{$t('models.cart.quantity.perDelivery')}}
        </small>
        <small class="badge badge-gray" v-if="availability() && !availability().availability_xd">
          {{ $t('models.cart.quantity.left') }} {{ availability().availability }} {{ $t('models.cart.quantity.of') }} {{ availability().availability_start }}
        </small>
      </div>
    `,
      data() {
        return {
          quantity: 0,
        };
      },
      props: {
        data: {
          type: Object,
          required: true,
        },
      },
      computed: {
        ...mapState("shop", ["cart"]),
      },
      mounted() {
        const v = getCartItemByPpmId(this.cart, get(this.data, "ppm.id"));
        this.quantity = v ? v.qta : 0;
      },
      watch: {
        cart() {
          const v = getCartItemByPpmId(this.cart, get(this.data, "ppm.id"));
          this.quantity = v ? v.qta : 0;
        },
      },
      methods: {
        variantId() {
          return get(this.data, "ppm.id");
        },
        suffix() {
          const pp = get(this.data, "ppl.productproducer");
          const pw_option = get(pp, "extra.pw_option", 1);
          return pw_option === 3
            ? `${getOptionLabel(pp.pw_unit, getPriceUnitOptions(this.$i18n))}`
            : `${getOptionLabel(pw_option, getPwOptions(this.$i18n), "short")}`;
        },
        availability() {
          if (get(this.data, "ppl.availability_limitless")) return;
          return {
            availability: get(this.data, "ppl.availability"),
            availability_start: get(this.data, "ppl.availability_start"),
            availability_xd: get(this.data, "ppl.availability_xd"),
          };
        },
        onSubmit(e) {
          this.$root.$emit("onCartAdd", {
            row: this.data,
            event: e,
            data: { quantity: this.quantity },
          });
        },
      },
    }),
  },

  props: {
    filter: {
      type: Object,
      default: () => {
        return {};
      },
    },
    bus: { type: Object },
  },

  computed: {
    ...mapGetters("connections", ["currencySymbol"]),

    lang: function () {
      return this.$i18n.locale;
    },
  },

  mounted() {
    const self = this;

    this.initializeFromQuery();

    this.loadProducts(this.currentPage);
    this.bus.$on("onSearch", (filter) => {
      // Reset to page 1 when searching
      self.currentPage = 1;
      self.updateUrl();
      self.loadProducts(1);
    });

    this.$root.$on("onCartAdd", this.onCartAdd);
  },

  beforeDestroy() {
    this.bus.$off("onSearch");
    this.$root.$off("onCartAdd", this.onCartAdd);
  },

  data: function () {
    return {
      cartLoading: false,
      loading: false,
      products: [],
      numPages: 1,
      currentPage: 1,
      queryFilters: {
        limit: {
          style: "query",
          operator: "=",
          term: parseInt(localStorage.getItem("l_var_rows")) || 12,
        },
        offset: {
          style: "query",
          operator: "=",
          term: 0,
        },
      },
      pagination: {
        count: 0,
        next: null,
        previous: null,
        limit: 12,
      },
      ordering: {
        style: "sort",
        term: null,
      },
    };
  },

  methods: {
    async loadProducts(page = null) {
      this.loading = true;
      const marketId = this.$store.state.shop.market.market.id;
      const familyId = this.$store.state.connections.current.family.id;

      // Update currentPage and URL before making the API call
      if (page) {
        this.currentPage = page;
      }
      this.updateUrl();

      let rqlFilter = this.queryFilters;
      const { search, categoryId, producerId, certificationsIds } = this.filter;

      if (search) {
        rqlFilter["search"] = {
          style: "query",
          term: search,
          operator: "=",
        };
      } else {
        delete rqlFilter["search"];
      }

      if (categoryId) {
        rqlFilter["category"] = {
          style: "query",
          term: categoryId,
          operator: "=",
        };
      } else {
        delete rqlFilter["category"];
      }

      if (producerId) {
        rqlFilter["producer"] = {
          style: "query",
          term: producerId,
          operator: "=",
        };
      } else {
        delete rqlFilter["producer"];
      }

      if (certificationsIds.length > 0) {
        let certIds = [];
        certificationsIds.map((cert) => {
          certIds.push(cert.value);
        });

        if (certIds.length > 1) {
          let certIdsString = "";
          certIds.forEach((id, index) => {
            certIdsString += `eq(certification,${id})`;
            if (index < certIds.length - 1) {
              certIdsString += ",";
            }
          });

          rqlFilter["certification"] = {
            style: "multiFunction",
            term: certIdsString,
            operator: "or",
          };
        }

        if (certIds.length === 1) {
          rqlFilter["certification"] = {
            style: "query",
            term: certIds[0],
            operator: "=",
          };
        }
      } else {
        delete rqlFilter["certification"];
      }

      if (page) {
        rqlFilter.offset = {
          style: "query",
          operator: "=",
          term: (page - 1) * this.queryFilters.limit.term,
        };
      }

      rqlFilter.limit = {
        style: "query",
        operator: "=",
        term: this.queryFilters.limit.term,
      };

      if (this.ordering.term) {
        rqlFilter.ordering = this.ordering;
      }

      let params = {
        endpoint: "family_market_variants",
        params: [familyId, marketId],
        filter: rqlFilter,
      };

      const response = await apiGetRQL(params);

      if (response && response.data) {
        this.numPages = Math.ceil(
          response.data.payload.Count / this.queryFilters.limit.term
        );

        this.products = response.data.payload.Items;

        this.pagination = {
          count: response.data.payload.Count,
          limit: parseInt(rqlFilter.limit.term),
          offset: parseInt(rqlFilter.offset.term),
        };

        if (!page) {
          this.currentPage =
            Math.floor(
              parseInt(rqlFilter.offset.term) / parseInt(rqlFilter.limit.term)
            ) + 1;
        }

        this.loading = false;
        scrollToTopOfpage(this);
      }
    },

    sortBy(param) {
      if (this.ordering.term === param) {
        this.ordering.term = "-" + param;
      } else {
        this.ordering.term = param;
      }
      this.updateUrl();
      this.loadProducts();
    },

    onCartAdd({ row, data }) {
      const self = this;

      const ppm_id = row.ppm.id;
      const quantity = data.quantity;

      this.$store
        .dispatch("shop/setCartQuantity", { items: [{ ppm_id, quantity }] })
        .then((response) => {
          self.$store.dispatch("toaster/add", {
            title: "OK!",
            text: self.$t("family.market.showcase.cart.msg_cart_update"),
            color: "success",
            autohide: true,
            delay: 1000,
          });
        });
    },

    getImage(data) {
      const variantImage = get(data, `ppl.image.image_thumb`);
      if (variantImage) {
        return variantImage;
      }
      const productImage = get(data, `ppl.productproducer.image.image_thumb`);
      if (productImage) {
        return productImage;
      }

      return "/loonity/img/placeholder.svg";
    },

    certifications(data) {
      const certifications = get(
        data,
        "ppl.productproducer.certifications",
        []
      );
      let cert_string = "";
      if (certifications.length > 0) {
        for (var i = 0; i < certifications.length; i++) {
          // certification logos
          /*
          cert_string += `<img src="${certifications[i].logo_thumb}"
          alt="${certifications[i].name}"
          title="${certifications[i].name}"
          class="productTemplateCertifications"/>`;
          */
          // certification names
          cert_string += `${certifications[i].name}`;
          if (i < certifications.length - 1) {
            cert_string += ", ";
          }
        }
      }
      return cert_string;
    },

    getPrice(data) {
      return formatPrice(data.ppm.price, this.currencySymbol).format();
    },

    basePriceUnit(data) {
      const price_base_unit = get(data, "price_base_unit");

      if (price_base_unit === null) return "";

      const unitOptions = getPriceUnitOptions(this.$i18n);
      let weight_unit = null;
      weight_unit = getOptionLabel(price_base_unit.unit, unitOptions);
      const unitPrice = formatPrice(
        price_base_unit.price,
        this.currencySymbol
      ).format();
      return unitPrice + " / " + weight_unit;
    },

    needPriceCorrection(data) {
      return (
        get(data, "ppl.pw", false) || get(data, "ppl.productproducer.pw", false)
      );
    },

    onCardClick(productId) {
      const familyId = this.$route.params.familyId;
      const marketId = this.$route.params.marketId;
      this.$router.push({
        name: "UserMarketProduct",
        params: { familyId, marketId, productId },
      });
    },

    logQuantities() {
      this.cartLoading = true;
      this.$nextTick(() => {
        const self = this;
        const inputs = this.$el.querySelectorAll(
          ".variantCartQuantityInput input"
        );
        let items = [];

        inputs.forEach((input) => {
          const ppm_id = input.dataset.variantId;
          const quantity = input.value;
          if (ppm_id && quantity) {
            items.push({ ppm_id, quantity });
          }
        });

        this.$store
          .dispatch("shop/setCartQuantity", { items: items })
          .then((response) => {
            this.cartLoading = false;
            self.$store.dispatch("toaster/add", {
              title: "OK!",
              text: self.$t("family.market.showcase.cart.msg_cart_update"),
              color: "success",
              autohide: true,
              delay: 1000,
            });
          });
      });
    },
    openProducerProfile(producerId) {
      const familyId = this.$route.params.familyId;
      const marketId = this.$route.params.marketId;

      this.$router.push({
        name: "UserMarketProducer",
        params: { familyId, marketId, producerId },
      });
    },

    initializeFromQuery() {
      const query = this.$route.query;

      // Restore page
      if (query.page) {
        this.currentPage = parseInt(query.page) || 1;
      }

      // Restore ordering
      if (query.sort) {
        this.ordering.term = query.sort;
      }

      // Restore filters from URL
      if (query.search) {
        this.filter.search = query.search;
      }
      if (query.category) {
        this.filter.categoryId = parseInt(query.category);
      }
      if (query.producer) {
        this.filter.producerId = parseInt(query.producer);
      }

      // Calculate offset from page and limit
      this.queryFilters.offset.term =
        (this.currentPage - 1) * this.queryFilters.limit.term;
    },

    updateUrl() {
      const query = {
        ...this.$route.query,
      };

      // Add/update page if not 1
      if (this.currentPage > 1) {
        query.page = this.currentPage.toString();
      } else {
        delete query.page;
      }

      // Add/update sort if present
      if (this.ordering.term) {
        query.sort = this.ordering.term;
      } else {
        delete query.sort;
      }

      // Add/update search filter
      if (this.filter.search) {
        query.search = this.filter.search;
      } else {
        delete query.search;
      }

      // Add/update category filter
      if (this.filter.categoryId) {
        query.category = this.filter.categoryId.toString();
      } else {
        delete query.category;
      }

      // Add/update producer filter
      if (this.filter.producerId) {
        query.producer = this.filter.producerId.toString();
      } else {
        delete query.producer;
      }

      // Only update if query actually changed
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ query }).catch(() => {});
      }
    },
  },
  watch: {
    "queryFilters.limit.term": async function (newVal, oldVal) {
      localStorage.setItem("l_var_rows", parseInt(newVal));
      this.pagination.limit = parseInt(newVal);
      await this.loadProducts();
    },
  },
};
</script>
